import Polylabel from 'polylabel';
import { Coordinate } from './coordinate';
import { renderEditIcon, switchCursor, renderBaseBorder, hideGroup, showGroup, arePointsClockwise } from '../helpers';
import { Border, BorderDirection, PlankType } from './border';
import { VisualOptions } from '../renderer';
import { AreaSplitter } from './area-splitter';

export class Planarea {
  _activated = false;
  events = [];
  activeBorder = null;
  visualOptions = [];

  // colors
  colorBoardFill = '#d0d0d0';
  colorBoardFillActive = '#b9b9b9';
  colorBoardFillActiveBreakerBoard = '#9c9c9c';
  colorBoardStroke = '#fff';
  colorOutlineStroke = '#d0d0d0';
  colorOutlineStrokeActive = '#b9b9b9';
  colorBorderStrokeActive = '#BE0000';
  colorBorderStrokeInactive = '#b9b9b9';
  colorCut = '#ababab';
  colorBackground = 'white';

  //widths
  widthBoard = 1;
  widthArea = 1;
  widthActiveBorder = 2;
  widthInactiveBorder = 1;
  widthCut = 2;

  constructor(options) {
    ({
      areaState: this.areaState,
      view: this.view,
      renderMode: this.renderMode = RenderMode.AreaEdit,
      additionalOptions: this.additionalOptions = {},
      onPlanareaActivated: this.onPlanareaActivated,
      onBorderActivated: this.onBorderActivated,
      onBaseBorderChanged: this.onBaseBorderChanged,
      onPlanareaSplitted: this.onPlanareaSplitted,
      onSplitViewChanged: this.onSplitViewChanged,
      debug: this.debug = false,
      translator: this.translator,
    } = options);
    this.group = options.group.createGroup();
    this.borders = [];
    this.events = [];
    this._activated = false;
    this.updatePlanarea();
  }

  updatePlanarea() {
    let pt = this.areaState.rawArea.tessellation;
    this.poly = new parkside.poly(
      pt.poly.points.map(point => {
        return { x: point.x, y: point.y };
      })
    );
    this.originalPoly = new parkside.poly(this.areaState.originalRawPoints);
    this.tessellation = new parkside.tessellation(this.poly, pt.transform.angle_rad, pt.rectwidth);
    this.originalTessellation = new parkside.tessellation(this.originalPoly, pt.transform.angle_rad, pt.rectwidth);
    const points = this._closePolygon(this.originalTessellation.poly.points);
    const isClockwise = arePointsClockwise(points);
    this.borders = [];
    for (let i = 0; i < points.length - 1; i++) {
      this.borders.push(
        new Border({
          planarea: this,
          startPoint: new Coordinate(points[i].x, points[i].y),
          endPoint: new Coordinate(points[i + 1].x, points[i + 1].y),
          borderDirection: isClockwise ? BorderDirection.CLOCKWISE : BorderDirection.COUNTERCLOCKWISE,
          index: i,
        })
      );
    }

    this.borders[this.areaState.rawArea.data.active_edge || 0].isBaseBorder = true;
  }

  render(visualOptions = []) {
    this.visualOptions = visualOptions;
    this.group.clear();

    this._renderBoards();
    this._renderBreakerBoards();
    this._renderOutline();
    if (this.renderMode === RenderMode.ShapeOnly) {
      // If we render trimLabels here, they could get overrided by other areas
      // Therefore own group for all trim labels is created in renderer
      // this._renderTrimLabels();
    } else {
      if (this._activated && this.renderMode === RenderMode.AreaEdit) {
        // here we render only labels for currenly active planarea
        this.renderTrimLabels(this.group.createGroup(), 25);
      }
      this._renderBorders();
      this._renderEdit();
      this._initEvents();
    }
    this.visualOptions = [];
  }

  editBaseBorder() {
    this.renderMode = RenderMode.BaseBorder;
  }

  editArea() {
    this.renderMode = RenderMode.AreaEdit;
  }

  editSplitPerpendicular() {
    this.renderMode = RenderMode.SplitPerpendicular;
  }

  editSplitParallel() {
    this.renderMode = RenderMode.SplitParallel;
  }

  _renderBoards() {
    const boardsGroup = this.group.createGroup();
    if (this.renderMode !== RenderMode.ShapeOnly) {
      if (!(this.activated && !this.activeBorder)) {
        switchCursor(boardsGroup, 'pointer');
      }
      boardsGroup.connect('click', () => {
        if (!this.activated || (this.activated && this.activeBorder)) {
          this.onPlanareaActivated(this);
        }
      });
    }

    if (!this.debug) this._clipGroup(boardsGroup, this.tessellation);
    this.areaState.rawArea.finalPlans.plans[this.areaState.currentPlanIndex].rows.forEach((rowplan, index) => {
      let rowgroup = boardsGroup.createGroup();
      rowplan.boards.forEach(bp => {
        const dnbPoints = this.tessellation.denormalizeBlock(
          rowplan.row,
          bp,
          this.areaState.rawArea.tessellation.transform
        ).points;
        rowgroup
          .createPolyline(this.view.convertGlobalToLocalAll(dnbPoints.concat(dnbPoints[0])))
          .setStroke({ color: this.colorBoardStroke, width: this.widthBoard })
          .setFill(this._activated ? this.colorBoardFillActive : this.colorBoardFill);
      });
      if (this.visualOptions.includes(VisualOptions.AnimateBoards) && this._activated) {
        rowgroup.rawNode.setAttribute(
          'style',
          'transition: opacity 0.5s;transition-delay: ' + index * 0.02 + 's; opacity: 0'
        );
      }
    });

    if (this.visualOptions.includes(VisualOptions.AnimateBoards) && this._activated) {
      setTimeout(() => {
        boardsGroup.children.forEach(row => (row.rawNode.style.opacity = '1'));
      }, 10);
    }

    if (this.areaState.rawArea.tessellation.cutoutPolygons) {
      this.areaState.rawArea.tessellation.cutoutPolygons.forEach(poly => {
        boardsGroup
          .createPolyline(this.view.convertGlobalToLocalAll(poly.points.concat(poly.points[0])))
          .setStroke({ color: this._getAreaOutlineStrokeColor(), width: this.widthArea })
          .setFill(this.colorBackground);
        boardsGroup.moveToFront();
      });
    }
  }

  _renderBreakerBoards() {
    const breakerBoardsGroup = this.group.createGroup();

    for (const breakerBoard of this.areaState.breakerBoards) {
      const pt = breakerBoard.rawArea.tessellation;
      const poly = new parkside.poly(
        pt.poly.points.map(point => {
          return { x: point.x, y: point.y };
        })
      );
      const tessellation = new parkside.tessellation(poly, pt.transform.angle_rad, pt.rectwidth);

      let breakerBoardGroup = breakerBoardsGroup.createGroup();
      if (!this.debug) this._clipGroup(breakerBoardGroup, tessellation);
      breakerBoard.rawArea.finalPlans.plans[0].rows.forEach((rowplan, index) => {
        let rowgroup = breakerBoardGroup.createGroup();
        rowplan.boards.forEach(bp => {
          let dnb_points = tessellation.denormalizeBlock(rowplan.row, bp, pt.transform).points;
          rowgroup
            .createPolyline(this.view.convertGlobalToLocalAll(dnb_points.concat(dnb_points[0])))
            .setStroke({ color: this.colorBoardStroke, width: this.widthBoard })
            .setFill(this._activated ? this.colorBoardFillActiveBreakerBoard : this.colorBoardFill);
        });
      });
    }
  }

  _renderOutline() {
    this.group
      .createPolyline(this.view.convertGlobalToLocalAll(this._closePolygon(this.originalTessellation.poly.points)))
      .setStroke({ color: this._getAreaOutlineStrokeColor(), width: this.widthArea });
    this.group.moveToFront();
  }

  _renderBorders() {
    if (this.activeBorder) {
      const startPoint = this.view.convertGlobalToLocal(this.activeBorder.startPoint);
      const endPoint = this.view.convertGlobalToLocal(this.activeBorder.endPoint);
      this.group
        .createLine({
          x1: startPoint.x,
          y1: startPoint.y,
          x2: endPoint.x,
          y2: endPoint.y,
        })
        .setStroke({ color: this.colorBorderStrokeActive, width: this.widthActiveBorder });
      this.group.moveToFront();
    }
  }

  renderTrimLabels(group, offset = 30) {
    const fontSize = 12;
    const hideLabelsLocalLengthTreshold = 0; // 0 = render all edge labels (even if some overlap could happen)
    this.borders.forEach(border => {
      const localEdgeLength = this.view.convertGlobalToLocalDistance(border.getLength());
      if (localEdgeLength < hideLabelsLocalLengthTreshold) {
        return;
      }
      const labelGroup = group.createGroup();
      let labels = [];
      for (const label of border.getBorderLabels().keys()) {
        labels.push(label);
      }
      const labelShortcuts = labels.map(label => this.translator.translate(label + '_shortcut')).join(', ');
      const labelFulltext = labels.map(label => this.translator.translate(label)).join(', ');
      const center = this.view.convertGlobalToLocal(
        border.getCenterWithOffset(this.view.convertLocalToGlobalDistance(offset))
      );
      const text = labelGroup
        .createText({
          x: center.x,
          y: center.y + fontSize / 2,
          text: labelShortcuts,
          align: 'middle',
        })
        .setFill(this.colorBorderStrokeActive)
        .setFont({ family: 'sans-serif', size: fontSize + 'px' });
      let transform = border.getFlattenSegment().slope;
      // reduce potential angles to the interval of [0,2Pi]
      transform = transform % (Math.PI * 2);
      // adjust rendering of upper right edges
      if (Math.PI / 2 <= transform && transform <= 1.5 * Math.PI) {
        transform += Math.PI;
      }
      labelGroup.setTransform([dojox.gfx.matrix.rotategAt(transform * (180 / Math.PI), center.x, center.y)]);
      // adjust styling and add svg title for default browser hover tooltip
      switchCursor(labelGroup, 'default');
      const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      title.innerHTML = labelFulltext;
      text.rawNode.prepend(title);
    });
  }

  _renderEdgeLengths() {
    const fontSize = 12;
    for (const border of this.borders) {
      const startPoint = this.view.convertGlobalToLocal(border.startPoint);
      const endPoint = this.view.convertGlobalToLocal(border.endPoint);
      const isSplitEdge = this.areaState.hasConnectionOnIndex(border.index);
      const center = this.view.convertGlobalToLocal(
        border.getCenterWithOffset(this.view.convertLocalToGlobalDistance(15))
      );
      const length = border.getLength();
      this.group
        .createLine({
          x1: startPoint.x,
          y1: startPoint.y,
          x2: endPoint.x,
          y2: endPoint.y,
        })
        .setStroke({
          color: isSplitEdge ? this.colorCut : this.colorBorderStrokeActive,
          width: isSplitEdge ? this.widthCut : this.widthInactiveBorder,
        });
      if (!isSplitEdge) {
        this.group
          .createText({
            x: center.x,
            y: center.y + fontSize / 2,
            text: Math.round(Math.abs(length)),
            align: 'middle',
          })
          .setFill(this.colorBorderStrokeActive)
          .setFont({ family: 'sans-serif', size: fontSize + 'px' });
      }
      this.group.moveToFront();
    }
  }

  _renderEdit() {
    this.groupEdit = this.group.createGroup();
    this.groupBorderEdit = this.group.createGroup();
    this.splitter ? this.splitter.clear() : null;

    this._addEditIconToGroup(this.groupEdit);

    if (this._activated) {
      if (this.additionalOptions.bordersSelectable && this.renderMode === RenderMode.AreaEdit) {
        this._addEditIconsToBorders(this.groupBorderEdit);
      }

      if (this.renderMode === RenderMode.BaseBorder) {
        this._addBaseSelectToBorders();
      }

      if (this.renderMode === RenderMode.SplitParallel || this.renderMode === RenderMode.SplitPerpendicular) {
        this.splitter = this.splitter
          ? this.splitter
          : new AreaSplitter({
              parentGroup: this.group,
              onPlanareaSplit: this.planareaSplited.bind(this),
              view: this.view,
              onViewChange: this.onSplitViewChanged,
            });
        this.splitter.render(this.renderMode, this);
      }
    }

    this.groupEdit.connect('click', e => {
      this.onPlanareaActivated(this);
    });
  }

  _addEditIconToGroup(group) {
    let iconCenter = this._findAreaEditIconPosition();
    renderEditIcon(group, iconCenter);
    group.rawNode.style.display = 'none';
    switchCursor(group, 'pointer');
  }

  _addBaseSelectToBorders() {
    this.borders.forEach(border => {
      const group = this.groupBorderEdit.createGroup();
      const hoverArea = group.createGroup();
      const iconArea = group.createGroup();
      this._createBorderHoverArea(hoverArea, border);
      switchCursor(group, 'pointer');
      renderBaseBorder(
        iconArea,
        this.view.convertGlobalToLocal(border.startPoint),
        this.view.convertGlobalToLocal(border.endPoint),
        border.isClockwise()
      );
      group.connect('click', e => {
        e.stopPropagation();
        this.onBaseBorderChanged(border);
      });

      if (!border.isBaseBorder) {
        hideGroup(iconArea);

        group.connect('mouseover', e => {
          e.stopPropagation();
          showGroup(iconArea);
        });

        group.connect('mouseout', e => {
          e.stopPropagation();
          hideGroup(iconArea);
        });
      }
    });
  }

  _addEditIconsToBorders() {
    this.borders.forEach(border => {
      const group = this.groupBorderEdit.createGroup();
      const iconArea = group.createGroup();
      const hoverArea = group.createGroup();
      this._createBorderHoverArea(hoverArea, border);

      group.connect('mouseover', e => {
        e.stopPropagation();
        if (this.activeBorder && this.activeBorder !== border) {
          iconArea.rawNode.style.display = 'block';
        }
      });

      group.connect('mouseout', e => {
        if (this.activeBorder) {
          iconArea.rawNode.style.display = 'none';
        }
      });

      renderEditIcon(iconArea, this.view.convertGlobalToLocal(border.getCenter()));
      iconArea.connect('click', () => {
        this.activeBorder = border;
        this.onBorderActivated(this.activeBorder);
      });
      iconArea.moveToFront();
      switchCursor(iconArea, 'pointer');
      if (this.activeBorder) {
        iconArea.rawNode.style.display = 'none';
      }
    });
  }

  _createBorderHoverArea(group, border) {
    group
      .createLine({
        x1: this.view.convertGlobalToLocal(border.startPoint).x,
        y1: this.view.convertGlobalToLocal(border.startPoint).y,
        x2: this.view.convertGlobalToLocal(border.endPoint).x,
        y2: this.view.convertGlobalToLocal(border.endPoint).y,
      })
      .setStroke({ color: 'rgba(0, 0, 0, 0)', width: 30 });
  }

  _findAreaEditIconPosition() {
    let polyCenter = new Polylabel(this._getPolygonPointsForPolylabel());
    return this.view.convertGlobalToLocal(new Coordinate(polyCenter));
  }

  _getAreaOutlineStrokeColor() {
    if (this._activated && this.activeBorder) {
      return this.colorOutlineStrokeActive;
    } else if (this._activated && !this.activeBorder) {
      return this.colorBorderStrokeActive;
    } else {
      return this.colorOutlineStroke;
    }
  }

  _closePolygon(points) {
    return points.concat(points[0]);
  }

  _initEvents() {
    this.group.connect('mouseover', e => {
      if (!this._activated || (this._activated && this.activeBorder)) {
        this.groupEdit.rawNode.style.display = 'block';
      }
    });
    this.group.connect('mouseout', e => {
      this.groupEdit.rawNode.style.display = 'none';
    });
  }

  _clipGroup(groupToClip, tessellation) {
    let clipPointArray = [];
    this.view.convertGlobalToLocalAll(tessellation.poly.points).forEach(coordinate => {
      clipPointArray.push(coordinate.x);
      clipPointArray.push(coordinate.y);
    });
    groupToClip.setClip({
      points: clipPointArray,
    });
  }

  _getPolygonPointsForPolylabel() {
    const polygons = [];
    polygons.push(
      this._closePolygon(this.tessellation.poly.points).map(point => {
        return [point.x, point.y];
      })
    );
    if (this.areaState.rawArea.tessellation.cutoutPolygons) {
      this.areaState.rawArea.tessellation.cutoutPolygons.forEach(poly => {
        polygons.push(
          this._closePolygon(poly.points).map(point => {
            return [point.x, point.y];
          })
        );
      });
    }

    return polygons;
  }

  get activated() {
    return this._activated;
  }

  activate() {
    this._activated = true;
    this.renderMode = RenderMode.AreaEdit;
  }

  deactivate() {
    this._activated = false;
  }

  deactivateBorder() {
    this.activeBorder = null;
  }

  isBoardLayoutButtjoint() {
    return this.areaState.rawArea.data.deck_type === 'buttjoint';
  }

  getBoardLayouts() {
    return this.areaState.rawArea.finalPlans.plans;
  }

  getIndexedBoardLayoutsSorted() {
    return this.areaState.indexedBoardLayoutsSorted;
  }

  getSelectedBoardLengths() {
    return this.areaState.rawArea.data.boards.map(boards => boards.length);
  }

  getUsedBoardLengthCounts() {
    return this.areaState.usedBoardLengthCounts;
  }

  getInstallationType() {
    switch (this.areaState.rawArea.data.deck_type) {
      case 'staggered':
        return InstallationType.staggered;
      case 'buttjoint':
        return InstallationType.buttjoint;
      default:
        return InstallationType.buttjoint;
    }
  }

  getBaseBorder() {
    return this.borders.find(border => border.isBaseBorder);
  }

  getBorders() {
    return this.borders;
  }

  getBoardsAngle() {
    return parseFloat(this.areaState.rawArea.data.angle_rad);
  }

  getFlattenPolygon() {
    return this.areaState.getFlattenPolygon();
  }

  getFlattenPolygonOfOriginal() {
    return this.areaState.getFlattenPolygonOfOriginal();
  }

  getPolygonPoints(closed = false) {
    return closed ? this._closePolygon(this.poly.points) : this.poly.points;
  }

  planareaSplited(segment) {
    this.onPlanareaSplitted(segment, this.areaState.id);
  }

  get id() {
    return this.areaState.id;
  }
}

export const RenderMode = Object.freeze({
  AreaEdit: Symbol('area'),
  BorderEdit: Symbol('border'),
  BaseBorder: Symbol('baseBorder'),
  SplitPerpendicular: Symbol('splitPerpendicular'),
  SplitParallel: Symbol('splitParallel'),
  ShapeOnly: Symbol('shapeonly'),
});

export const InstallationType = Object.freeze({
  buttjoint: Symbol('buttjoint'),
  staggered: Symbol('staggered'),
});
